import React, { useState, useEffect, useRef, useCallback } from 'react';
import AddTaskButton from './tasks/AddTaskButton';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import { Warehouse, Supplier } from '../types';
import '../OrderDetailsDialog.css';
import '../core.css';
import '../purchaseOrders.css';

interface PurchaseOrder {
  id?: number;
  po_number?: string;
  supplier: string;
  order_items: string;
  requested_date?: string;
  estimated_date: string | null;
  ship_to_address: string;
  notes: string;
  status: string;
  inventory_transfer?: boolean;
  last_updated?: string;
}

interface PurchaseOrderItem {
  product_id: number;
  variation_id: number;
  product_name: string;
  supplier_part_number?: string;
  fdc_wms_number?: string;
  sku: string;
  gtin?: string;
  warehouse_stock: number;
  total_stock: number;
  target_stock: number;
  stock_days: string;
  recommended_quantity: number;
  cost_price: number;
  avg_sales_per_month: number;
  target_stock_days?: number;
  selected: boolean;
  quantity: number;
  locked?: boolean;
}

type SortKey = 'product_name' | 'sku' | 'supplier_part_number' | 'warehouse_stock' | 'total_stock' | 'target_stock' | 'stock_days' | 'recommended_quantity';
type SortOrder = 'asc' | 'desc';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

interface PurchaseOrderNote {
  id: number;
  purchase_order_id: number;
  note: string;
  added_by: string;
  date_added: string;
}

interface PurchaseOrderDialogProps {
  purchaseOrder: PurchaseOrder | null;
  isNew: boolean;
  onClose: () => void;
}

const PurchaseOrderDialog: React.FC<PurchaseOrderDialogProps> = ({ purchaseOrder, isNew, onClose }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('');
  const [selectedWarehouse, setSelectedWarehouse] = useState<number | null>(null);
  const [estimatedDate, setEstimatedDate] = useState<string>('');
  const [poNumber, setPoNumber] = useState<string>('');
  const [poValue, setPoValue] = useState<number>(0);
  const [isEditingPoNumber, setIsEditingPoNumber] = useState<boolean>(false);

  const [status, setStatus] = useState<string>('Draft');
  const [notes, setNotes] = useState<PurchaseOrderNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [suggestions, setSuggestions] = useState<PurchaseOrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [customShipToAddress, setCustomShipToAddress] = useState('');
  const [isEditingShipTo, setIsEditingShipTo] = useState(false);
  const [inventoryTransfer, setInventoryTransfer] = useState(false);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'stock_days', order: 'asc' });
  const [itemFilter, setItemFilter] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  // Snapshot of the loaded state, used to detect real unsaved changes on close
  const initialSnapshotRef = useRef<string | null>(null);

  const makeSnapshot = (
    supplier: string,
    poStatus: string,
    estDate: string,
    invTransfer: boolean,
    shipTo: string,
    items: PurchaseOrderItem[]
  ) =>
    JSON.stringify({
      supplier,
      poStatus,
      estDate,
      invTransfer,
      shipTo,
      items: items
        .filter((i) => i.selected)
        .map((i) => `${i.product_id}-${i.variation_id || 0}:${i.quantity}`)
        .sort(),
    });

  const fetchNotes = async (poId: number) => {
    try {
      const response = await api.get(`/purchase-orders-notes?purchase_order_id=${poId}`);
      setNotes(response.data.notes);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };

  const loadPurchaseOrder = useCallback(async () => {
    if (!purchaseOrder) return;

    setPoNumber(purchaseOrder.po_number || '');
    setSelectedSupplier(purchaseOrder.supplier);
    setEstimatedDate(purchaseOrder.estimated_date ? new Date(purchaseOrder.estimated_date).toISOString().split('T')[0] : '');
    setStatus(purchaseOrder.status);
    setCustomShipToAddress(purchaseOrder.ship_to_address);
    setInventoryTransfer(purchaseOrder.inventory_transfer || false);

    // Parse ship_to_address to find warehouse
    const addressLines = purchaseOrder.ship_to_address.split('\n');
    const warehouseName = addressLines[0]?.trim();
    const warehouse = warehouses.find(w =>
      (w.ship_from_name && w.ship_from_name === warehouseName) ||
      w.name === warehouseName
    );
    if (warehouse) {
      setSelectedWarehouse(warehouse.id);
    } else {
      // Fallback: use the first warehouse if no match found
      const defaultWarehouse = warehouses[0];
      if (defaultWarehouse) {
        setSelectedWarehouse(defaultWarehouse.id);
      }
    }

    // Load notes
    fetchNotes(purchaseOrder.id!);

    // Parse order items and load suggestions if supplier is selected
    const warehouseId = warehouse?.id || warehouses[0]?.id;
    let loadedItems: PurchaseOrderItem[] = [];
    if (purchaseOrder.order_items && purchaseOrder.supplier && warehouseId) {
      loadedItems = await loadSuggestionsAndParseItems(purchaseOrder.supplier, purchaseOrder.order_items, warehouseId);
    }

    // Capture the just-loaded state so closing without edits doesn't warn
    initialSnapshotRef.current = makeSnapshot(
      purchaseOrder.supplier,
      purchaseOrder.status,
      purchaseOrder.estimated_date ? new Date(purchaseOrder.estimated_date).toISOString().split('T')[0] : '',
      purchaseOrder.inventory_transfer || false,
      purchaseOrder.ship_to_address,
      loadedItems
    );
  }, [purchaseOrder, warehouses]);

  const loadSuggestionsAndParseItems = async (supplier: string, orderItemsStr: string, warehouseId: number) => {
    try {
      setLoading(true);
      const response = await api.get(`/purchase-order-suggestions?supplier_name=${encodeURIComponent(supplier)}&warehouse_id=${warehouseId}`);
      const fetchedSuggestions = response.data.suggestions || [];

      // Parse existing order items
      const existingItems: { [key: string]: number } = {};
      orderItemsStr.split(',').forEach((item: string) => {
        const [productInfo, quantityStr] = item.split(':');
        if (productInfo && quantityStr) {
          const [productKey] = productInfo.split('|'); // Extract just the key part
          existingItems[productKey.trim()] = parseInt(quantityStr.trim(), 10);
        }
      });

      // Mark suggestions as selected and set quantities
      const updatedSuggestions = fetchedSuggestions.map((suggestion: PurchaseOrderItem) => {
        const itemKey = `${suggestion.product_id}-${suggestion.variation_id || 0}`;
        const wasSelected = existingItems[itemKey] !== undefined;
        return {
          ...suggestion,
          selected: wasSelected,
          quantity: existingItems[itemKey] || suggestion.recommended_quantity,
          locked: true // Lock ALL items for existing POs
        };
      });

      setSuggestions(updatedSuggestions);
      calculatePOValue(updatedSuggestions);
      return updatedSuggestions as PurchaseOrderItem[];
    } catch (error) {
      console.error('Failed to load suggestions and parse items:', error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Close on Escape (respects the unsaved-changes check)
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showDeleteConfirm && !showEmailTemplate) {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  useEffect(() => {
    if (isNew) {
      // Set default PO number to current date in YY-MM-DD format
      const now = new Date();
      const yy = String(now.getFullYear()).slice(-2);
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const dd = String(now.getDate()).padStart(2, '0');
      setPoNumber(`${yy}-${mm}-${dd}`);
    }
  }, [isNew]);

  useEffect(() => {
    if (!isNew && purchaseOrder) {
      loadPurchaseOrder();
    }
  }, [purchaseOrder, isNew, warehouses, loadPurchaseOrder]);

  // Set inventory transfer default for Gelinas supplier
  useEffect(() => {
    if (isNew && selectedSupplier === 'Gelinas') {
      setInventoryTransfer(true);
    }
  }, [selectedSupplier, isNew]);

  const fetchInitialData = async () => {
    try {
      const [suppliersRes, warehousesRes, statusesRes] = await Promise.all([
        api.get('/neon-stock'),
        api.get('/warehouses'),
        api.get('/purchase-order-statuses')
      ]);

      setSuppliers(suppliersRes.data.suppliers || []);
      setWarehouses(warehousesRes.data.warehouses || []);
      setStatuses(statusesRes.data.statuses || []);
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
    }
  };



  const calculatePOValue = (items: PurchaseOrderItem[]) => {
    const value = items
      .filter(item => item.selected)
      .reduce((sum, item) => sum + (item.quantity * item.cost_price), 0);
    setPoValue(value);
  };

  const handleSupplierChange = async (supplier: string) => {
    setSelectedSupplier(supplier);
    setSuggestions([]);
    if (supplier && selectedWarehouse) {
      if (!isNew && purchaseOrder?.order_items) {
        // For existing POs, parse the saved items
        await loadSuggestionsAndParseItems(supplier, purchaseOrder.order_items, selectedWarehouse);
      } else {
        // For new POs, just fetch suggestions
        await fetchSuggestionsForSupplier(supplier, selectedWarehouse);
      }
    }
  };

  const handleWarehouseChange = async (warehouseId: number) => {
    setSelectedWarehouse(warehouseId);
    if (selectedSupplier && warehouseId) {
      if (!isNew && purchaseOrder?.order_items) {
        // For existing POs, parse the saved items
        await loadSuggestionsAndParseItems(selectedSupplier, purchaseOrder.order_items, warehouseId);
      } else {
        // For new POs, just fetch suggestions
        await fetchSuggestionsForSupplier(selectedSupplier, warehouseId);
      }
    }
  };

  const fetchSuggestionsForSupplier = async (supplier: string, warehouseId: number) => {
    setLoading(true);
    try {
      const response = await api.get(`/purchase-order-suggestions?supplier_name=${encodeURIComponent(supplier)}&warehouse_id=${warehouseId}`);
      const items = response.data.suggestions.map((item: any) => ({
        ...item,
        avg_sales_per_month: item.avg_sales_per_month,
        selected: false,
        quantity: item.recommended_quantity,
        locked: false
      }));
      setSuggestions(items);
      calculatePOValue(items);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelection = (productId: number, variationId: number) => {
    const newSuggestions = suggestions.map(item =>
      item.product_id === productId && item.variation_id === variationId
        ? { ...item, selected: !item.selected, locked: item.selected ? item.locked : false } // Unlock newly selected items
        : item
    );
    setSuggestions(newSuggestions);
    calculatePOValue(newSuggestions);
  };

  const handleQuantityChange = (productId: number, variationId: number, quantity: number) => {
    const newSuggestions = suggestions.map(item =>
      item.product_id === productId && item.variation_id === variationId
        ? { ...item, quantity }
        : item
    );
    setSuggestions(newSuggestions);
    calculatePOValue(newSuggestions);
  };

  const toggleQuantityLock = (productId: number, variationId: number) => {
    const newSuggestions = suggestions.map(item =>
      item.product_id === productId && item.variation_id === variationId
        ? { ...item, locked: !item.locked }
        : item
    );
    setSuggestions(newSuggestions);
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !purchaseOrder?.id) return;

    try {
      await api.post('/purchase-orders-notes', {
        purchase_order_id: purchaseOrder.id,
        note: newNote,
        added_by: user?.email || 'Unknown'
      });
      setNewNote('');
      fetchNotes(purchaseOrder.id);
      showToast('Note added successfully', 'success');
    } catch (error) {
      console.error('Failed to add note:', error);
      showToast('Failed to add note', 'error');
    }
  };

  const handleSave = async (markAsSent = false) => {
    if (!selectedSupplier || !selectedWarehouse) {
      showToast('Please select a supplier and warehouse', 'error');
      return;
    }

    const selectedItems = suggestions.filter(item => item.selected);
    if (selectedItems.length === 0) {
      showToast('Please select at least one item', 'error');
      return;
    }

    setSaving(true);
    try {
      const shipToAddress = customShipToAddress ||
        (() => {
          const warehouse = warehouses.find(w => w.id === selectedWarehouse);
          return warehouse ? `${warehouse.ship_from_name || warehouse.name}\n${warehouse.ship_from_address1}${warehouse.ship_from_address2 ? `\n${warehouse.ship_from_address2}` : ''}\n${warehouse.ship_from_city}, ${warehouse.ship_from_state} ${warehouse.ship_from_zipcode}` : '';
        })();

      const orderItems = selectedItems.map(item =>
        `${item.product_id}-${item.variation_id || 0}|${item.product_name}:${item.quantity}`
      ).join(',');

      const poData = {
        po_number: poNumber || null,
        supplier: selectedSupplier,
        order_items: orderItems,
        estimated_date: estimatedDate || null,
        ship_to_address: shipToAddress,
        notes: '',
        status: markAsSent ? 'Sent to Supplier' : status,
        inventory_transfer: inventoryTransfer,
        ...(purchaseOrder?.id && { id: purchaseOrder.id })
      };

      if (isNew) {
        await api.post('/purchase-orders', poData);
        showToast('Purchase order created successfully', 'success');
      } else {
        await api.put('/purchase-orders', poData);
        showToast('Purchase order updated successfully', 'success');
      }

      // Update estimated restock dates if date provided and not inventory transfer
      if (estimatedDate && !inventoryTransfer) {
        for (const item of selectedItems) {
          try {
            await api.post('/update-estimated-restock-date', {
              product_id: item.product_id,
              variation_id: item.variation_id || 0,
              estimated_restock_date: estimatedDate,
              website_message: null
            });
          } catch (error) {
            console.error('Failed to update restock date:', error);
          }
        }
        showToast('Estimated restock dates updated on WooCommerce', 'success');
      }

      if (markAsSent) {
        // Generate email template
        const emailTemplateText = generateEmailTemplate(selectedItems, poData);
        setEmailTemplate(emailTemplateText);
        setShowEmailTemplate(true);
        // Don't close the dialog yet - let user see the email template
        return;
      }

      onClose();
    } catch (error) {
      console.error('Failed to save purchase order:', error);
      showToast('Failed to save purchase order', 'error');
    } finally {
      setSaving(false);
    }
  };

  const generateEmailTemplate = (items: PurchaseOrderItem[], po: any) => {
    const itemLines = items.map(item =>
      `${item.quantity}x ${item.supplier_part_number || item.sku} ${item.product_name}`
    ).join('\n');

    return `Subject: Purchase Order ${po.po_number || `PO-${po.id || 'NEW'}`} for ${po.supplier}

Dear ${po.supplier},

Please find our purchase order details below:

Purchase Order Number: ${po.po_number || `PO-${po.id || 'NEW'}`}
Ship To Address:
${po.ship_to_address}

Items:
${itemLines}

Please confirm receipt of this order and provide an estimated shipping date.

Best regards,
${user?.email || 'Your Company'}
`;
  };

  const handleGenerateFdcAsn = async () => {
    const selectedItems = suggestions.filter(item => item.selected);
    if (selectedItems.length === 0) {
      showToast('Please select at least one item', 'error');
      return;
    }

    // Company and facility codes are set in the FDC Hub
    let asnCodes: { company: string; facility: string };
    try {
      const res = await api.get('/settings?key=fdc_asn');
      asnCodes = { company: res.data.fdc_asn_company, facility: res.data.fdc_asn_facility };
    } catch (error) {
      console.error('Failed to load FDC ASN codes:', error);
      showToast('Could not load FDC ASN codes from the FDC Hub settings', 'error');
      return;
    }

    const poData = {
      po_number: poNumber || `PO-${purchaseOrder?.id || 'NEW'}`,
      supplier: selectedSupplier,
      ship_to_address: customShipToAddress || (() => {
        const warehouse = warehouses.find(w => w.id === selectedWarehouse);
        return warehouse ? `${warehouse.ship_from_name || warehouse.name}\n${warehouse.ship_from_address1}${warehouse.ship_from_address2 ? `\n${warehouse.ship_from_address2}` : ''}\n${warehouse.ship_from_city}, ${warehouse.ship_from_state} ${warehouse.ship_from_zipcode}` : '';
      })()
    };

    const csvContent = generateFdcAsn(selectedItems, poData, asnCodes);

    // Create and download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `FDC_ASN_${poData.po_number}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast('FDC ASN CSV downloaded successfully', 'success');
  };

  const generateFdcAsn = (
    items: PurchaseOrderItem[],
    po: any,
    codes: { company: string; facility: string }
  ) => {
    const csvHeaders = 'company,facility,order number,item number,qty,planned date,product SKU\n';
    const currentDate = new Date();
    const plannedDate = new Date(currentDate);
    plannedDate.setDate(currentDate.getDate() + 7);
    const formattedDate = `${(plannedDate.getMonth() + 1).toString().padStart(2, '0')}/${plannedDate.getDate().toString().padStart(2, '0')}/${plannedDate.getFullYear().toString().slice(-2)}`;

    const csvRows = items.map(item =>
      `${codes.company},${codes.facility},${po.po_number},${item.fdc_wms_number || 'N/A'},${item.quantity},${formattedDate},${item.sku}`
    ).join('\n');

    return csvHeaders + csvRows;
  };

  const handleClose = () => {
    let hasChanges: boolean;
    if (isNew) {
      hasChanges = suggestions.some((item) => item.selected) || !!newNote.trim();
    } else {
      const current = makeSnapshot(
        selectedSupplier,
        status,
        estimatedDate,
        inventoryTransfer,
        customShipToAddress,
        suggestions
      );
      hasChanges = !!newNote.trim() || (initialSnapshotRef.current !== null && current !== initialSnapshotRef.current);
    }

    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const handleDelete = async () => {
    if (!purchaseOrder?.id) return;

    setShowDeleteConfirm(false);
    try {
      await api.delete(`/purchase-orders?id=${purchaseOrder.id}`);
      showToast('Purchase order deleted successfully', 'success');
      onClose();
    } catch (error) {
      console.error('Failed to delete purchase order:', error);
      showToast('Failed to delete purchase order', 'error');
    }
  };

  const calculateStockAtDelivery = (item: PurchaseOrderItem) => {
    if (!estimatedDate || !estimatedDate.trim()) return item.total_stock;

    try {
      const today = new Date();
      const deliveryDate = new Date(estimatedDate);

      // Check if delivery date is valid
      if (isNaN(deliveryDate.getTime())) return item.total_stock;

      const daysDiff = Math.max(0, (deliveryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      const monthsDiff = daysDiff / 30;

      const salesDuringPeriod = (item.avg_sales_per_month || 0) * monthsDiff;

      const result = Math.max(0, (item.total_stock || 0) - salesDuringPeriod + (item.quantity || 0));

      return isNaN(result) ? item.total_stock : result;
    } catch (error) {
      console.error('Error calculating stock at delivery:', error);
      return item.total_stock;
    }
  };

  const getStockDeliveryClass = (stockAtDelivery: number, targetStock: number) => {
    if (stockAtDelivery > targetStock) {
      return 'stock-delivery-high'; // Green
    } else if (stockAtDelivery > targetStock - 1) {
      return 'stock-delivery-medium'; // Yellow
    } else {
      return 'stock-delivery-low'; // Red
    }
  };

  const handleSort = (key: SortKey) => {
    setSortConfig(prev => ({
      key,
      order: prev.key === key && prev.order === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleApplyRecommended = () => {
    const newSuggestions = suggestions.map((item) =>
      item.recommended_quantity > 0
        ? { ...item, selected: true, quantity: item.recommended_quantity, locked: false }
        : item
    );
    setSuggestions(newSuggestions);
    calculatePOValue(newSuggestions);
    const applied = newSuggestions.filter((i) => i.recommended_quantity > 0).length;
    showToast(`Applied recommended quantities to ${applied} item${applied === 1 ? '' : 's'}`, 'success');
  };

  const sortedSuggestions = [...suggestions].sort((a, b) => {
    // Selected items always pin to the top so the PO contents are obvious
    if (a.selected !== b.selected) return a.selected ? -1 : 1;
    const { key, order } = sortConfig;
    let aValue: any = a[key];
    let bValue: any = b[key];

    // Handle numeric fields
    if (key === 'warehouse_stock' || key === 'total_stock' || key === 'target_stock' || key === 'recommended_quantity') {
      aValue = Number(aValue);
      bValue = Number(bValue);
    } else if (key === 'stock_days') {
      aValue = parseFloat(aValue);
      bValue = parseFloat(bValue);

      // Handle NaN values (from "N/A" or invalid stock_days)
      if (isNaN(aValue) && isNaN(bValue)) return 0;
      if (isNaN(aValue)) return order === 'asc' ? 1 : -1; // NaN goes to end for asc, beginning for desc
      if (isNaN(bValue)) return order === 'asc' ? -1 : 1; // NaN goes to end for asc, beginning for desc
    }

    // Handle empty/null supplier_part_number
    if (key === 'supplier_part_number') {
      aValue = aValue || '';
      bValue = bValue || '';
    }

    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  const filterText = itemFilter.trim().toLowerCase();
  const visibleSuggestions = filterText
    ? sortedSuggestions.filter(
        (item) =>
          item.selected || // never hide items already on the PO
          item.product_name.toLowerCase().includes(filterText) ||
          item.sku.toLowerCase().includes(filterText) ||
          (item.supplier_part_number || '').toLowerCase().includes(filterText)
      )
    : sortedSuggestions;

  const selectedCount = suggestions.filter((s) => s.selected).length;

  return (
    <div className="order-dialog-overlay">
      <div className="order-dialog" ref={dialogRef}>
        <button className="close-button" onClick={handleClose}>✕</button>
        {!isNew && purchaseOrder?.id && (
          <AddTaskButton
            className="tk-add-task-dialog"
            text={`Follow up with ${purchaseOrder.supplier} on ${purchaseOrder.po_number || `PO-${purchaseOrder.id}`}`}
            link={{
              link_type: 'purchase_order',
              link_ref: String(purchaseOrder.id),
              link_label: purchaseOrder.po_number || `PO-${purchaseOrder.id}`,
            }}
          />
        )}

        <div className="po-dialog-header">
          <h2>
            {isEditingPoNumber ? (
              <input
                type="text"
                value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)}
                onBlur={() => setIsEditingPoNumber(false)}
                onKeyPress={(e) => e.key === 'Enter' && setIsEditingPoNumber(false)}
                className="po-number-edit-input"
                autoFocus
              />
            ) : (
              poNumber || (purchaseOrder?.id ? `PO-${purchaseOrder.id}` : 'New Purchase Order')
            )}
          </h2>
          {isNew && !isEditingPoNumber && (
            <button
              className="edit-po-button"
              onClick={() => setIsEditingPoNumber(true)}
              title="Edit PO Number"
            >
              ✏️
            </button>
          )}
          <span className="po-value-pill" title="Total value of selected items">
            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(poValue)}
          </span>
        </div>

        <div className="po-meta-grid">
          <div className="po-field">
            <span className="po-field-label">Supplier</span>
            <select
              value={selectedSupplier}
              onChange={(e) => handleSupplierChange(e.target.value)}
              disabled={!isNew}
            >
              <option value="">Select Supplier</option>
              {suppliers.map((supplier) => (
                <option key={supplier.name} value={supplier.name}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div className="po-field">
            <span className="po-field-label">Ship To Warehouse</span>
            <select
              value={selectedWarehouse || ''}
              onChange={(e) => handleWarehouseChange(Number(e.target.value))}
              disabled={!isNew}
            >
              <option value="">Select Warehouse</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
          </div>

          <div className="po-field">
            <span className="po-field-label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {statuses.map((statusOption) => (
                <option key={statusOption} value={statusOption}>
                  {statusOption}
                </option>
              ))}
            </select>
          </div>

          <div className="po-field">
            <span className="po-field-label">Est. Delivery Date</span>
            <input
              type="date"
              value={estimatedDate}
              onChange={(e) => setEstimatedDate(e.target.value)}
            />
          </div>

          <div className="po-field">
            <span className="po-field-label">Options</span>
            <label className="po-checkbox-field">
              <input
                type="checkbox"
                checked={inventoryTransfer}
                onChange={(e) => setInventoryTransfer(e.target.checked)}
              />
              Inventory Transfer
            </label>
          </div>
        </div>

        <div className="po-two-col">
          {selectedWarehouse && (() => {
            const warehouse = warehouses.find(w => w.id === selectedWarehouse);
            const displayAddress = customShipToAddress || (warehouse ? `${warehouse.ship_from_name || warehouse.name}\n${warehouse.ship_from_address1}${warehouse.ship_from_address2 ? `\n${warehouse.ship_from_address2}` : ''}\n${warehouse.ship_from_city}, ${warehouse.ship_from_state} ${warehouse.ship_from_country}\n${warehouse.ship_from_zipcode}` : '');

            return (
              <div className="po-panel">
                <div className="po-panel-header">
                  <h4>Ship To Address</h4>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isEditingShipTo && !customShipToAddress) {
                        // Initialize with current warehouse address when starting to edit
                        const warehouse = warehouses.find(w => w.id === selectedWarehouse);
                        if (warehouse) {
                          const warehouseAddress = `${warehouse.ship_from_name || warehouse.name}\n${warehouse.ship_from_address1}${warehouse.ship_from_address2 ? `\n${warehouse.ship_from_address2}` : ''}\n${warehouse.ship_from_city}, ${warehouse.ship_from_state} ${warehouse.ship_from_zipcode}`;
                          setCustomShipToAddress(warehouseAddress);
                        }
                      }
                      setIsEditingShipTo(!isEditingShipTo);
                    }}
                    className="edit-address-btn"
                    title="Edit shipping address"
                  >
                    ✏️
                  </button>
                </div>
                {isEditingShipTo ? (
                  <textarea
                    value={customShipToAddress}
                    onChange={(e) => setCustomShipToAddress(e.target.value)}
                    placeholder="Enter custom shipping address"
                    className="address-textarea"
                    rows={4}
                  />
                ) : (
                  <div className="po-address-display">
                    {displayAddress.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <div className="po-panel">
            <div className="po-panel-header">
              <h4>Notes</h4>
            </div>
            {notes.length > 0 ? (
              <ul className="notes-list">
                {notes.map((note) => (
                  <li key={note.id} className="note-item">
                    <span className="note-tag">
                      {note.note}
                      <div className="note-meta">
                        {note.added_by}, {new Date(note.date_added).toLocaleString()}
                      </div>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="po-no-notes">No notes</p>
            )}
            <div className="add-note-container">
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Enter note..."
                className="note-input"
              />
              <button className="action-button" onClick={handleAddNote} disabled={!newNote.trim()}>
                Add Note
              </button>
            </div>
          </div>
        </div>

        <div className="order-items-section">
          <div className="po-items-toolbar">
            <h4>Purchase Order Items</h4>
            {suggestions.length > 0 && (
              <>
                <span className="po-items-summary">
                  {selectedCount} of {suggestions.length} selected
                </span>
                <button
                  type="button"
                  className="po-apply-recommended"
                  onClick={handleApplyRecommended}
                  title="Select every item with a recommended quantity and apply it"
                >
                  ✓ Apply Recommended Quantities
                </button>
                <input
                  type="text"
                  className="po-items-search"
                  placeholder="Filter by name, SKU, or part #…"
                  value={itemFilter}
                  onChange={(e) => setItemFilter(e.target.value)}
                />
              </>
            )}
          </div>
          {loading ? (
            <p>Loading suggestions...</p>
          ) : suggestions.length === 0 ? (
            <p className="po-no-notes">Select a supplier and warehouse to load item suggestions</p>
          ) : (
            <table className="order-items-table">
              <thead>
                <tr>
                  <th>Select</th>
                  <th>Quantity</th>
                  <th onClick={() => handleSort('product_name')} style={{ cursor: 'pointer' }}>
                    Product Name {sortConfig.key === 'product_name' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('sku')} style={{ cursor: 'pointer' }}>
                    SKU {sortConfig.key === 'sku' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('supplier_part_number')} style={{ cursor: 'pointer' }}>
                    Supplier PN {sortConfig.key === 'supplier_part_number' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('total_stock')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                    Current Stock {sortConfig.key === 'total_stock' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('warehouse_stock')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                    {warehouses.find(w => w.id === selectedWarehouse)?.name} Stock {sortConfig.key === 'warehouse_stock' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('target_stock')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                    Target Stock {sortConfig.key === 'target_stock' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th onClick={() => handleSort('stock_days')} style={{ cursor: 'pointer', textAlign: 'center' }}>
                    Stock Days {sortConfig.key === 'stock_days' && (sortConfig.order === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ textAlign: 'center' }}>Stock At Delivery Date</th>
                </tr>
              </thead>
              <tbody>
                {visibleSuggestions.map((item) => (
                  <tr
                    key={`${item.product_id}-${item.variation_id}`}
                    className={item.selected ? 'po-row-selected' : ''}
                  >
                    <td>
                      <input
                        type="checkbox"
                        checked={item.selected}
                        onChange={() => handleItemSelection(item.product_id, item.variation_id)}
                      />
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.product_id, item.variation_id, Number(e.target.value))}
                          className="quantity-input"
                          min="0"
                          disabled={item.locked}
                        />
                        <button
                          type="button"
                          onClick={() => toggleQuantityLock(item.product_id, item.variation_id)}
                          className="quantity-lock-btn"
                          title={item.locked ? 'Unlock quantity for editing' : 'Lock quantity'}
                        >
                          {item.locked ? '✏️' : '🔒'}
                        </button>
                      </div>
                    </td>
                    <td>{item.product_name}</td>
                    <td>{item.sku}</td>
                    <td>{item.supplier_part_number || 'N/A'}</td>
                    <td style={{ textAlign: 'center' }}>{item.total_stock}</td>
                    <td style={{ textAlign: 'center' }}>{item.warehouse_stock}</td>
                    <td style={{ textAlign: 'center' }}>{item.target_stock}</td>
                    <td style={{ textAlign: 'center' }}>{item.stock_days}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`stock-delivery-chip ${getStockDeliveryClass(calculateStockAtDelivery(item), item.target_stock)}`}>
                        {calculateStockAtDelivery(item).toFixed(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="order-dialog-actions">
          {warehouses.find(w => w.id === selectedWarehouse)?.name === 'FDC' && (
            <button
              className="action-button"
              onClick={handleGenerateFdcAsn}
            >
              Generate FDC ASN
            </button>
          )}
          {!isNew && (
            <button
              className="action-button delete-button"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete PO
            </button>
          )}
          <button
            className="action-button"
            onClick={() => handleSave(false)}
            disabled={saving || !selectedSupplier || suggestions.filter(s => s.selected).length === 0}
          >
            {saving ? 'Saving...' : 'Save PO'}
          </button>
          <button
            className="action-button primary-button"
            onClick={() => handleSave(true)}
            disabled={saving || !selectedSupplier || suggestions.filter(s => s.selected).length === 0}
          >
            {saving ? 'Saving...' : 'Save PO & Send'}
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="supplier-dialog-overlay">
            <div className="supplier-dialog">
              <h3>Delete Purchase Order</h3>
              <p>Are you sure you want to delete this purchase order? This action cannot be undone.</p>
              <div className="supplier-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="action-button delete-button"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showEmailTemplate && (
          <div className="supplier-dialog-overlay">
            <div className="email-template-dialog">
              <h3>Purchase Order Email Template</h3>
              <div className="email-template-content">
                <pre>{emailTemplate}</pre>
              </div>
              <div className="supplier-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={() => {
                    setShowEmailTemplate(false);
                    onClose();
                  }}
                >
                  Close
                </button>
                <button
                  className="action-button"
                  onClick={() => {
                    navigator.clipboard.writeText(emailTemplate);
                    showToast('Email template copied to clipboard', 'success');
                  }}
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderDialog;